import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AUTH } from "@/constants/testIds";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Truck } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const routeFor = (user) => (user.role === "admin" ? "/admin" : "/my-shipments");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = tab === "login" ? await login(email, password) : await register(name, email, password);
      navigate(routeFor(user));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <Navbar />
      <div className="mx-auto flex max-w-md flex-col px-4 py-14">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-orange-600">
            <Truck className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-2xl font-black tracking-tight">Welcome to Go Deliver</h1>
          <p className="text-sm text-slate-400">Sign in or create a customer account</p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-slate-900/70 p-6">
          <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(""); }}>
            <TabsList className="grid w-full grid-cols-2 bg-slate-800">
              <TabsTrigger data-testid={AUTH.tabLogin} value="login">Login</TabsTrigger>
              <TabsTrigger data-testid={AUTH.tabSignup} value="signup">Sign Up</TabsTrigger>
            </TabsList>

            <form onSubmit={submit} className="mt-5 space-y-4">
              <TabsContent value="signup" className="m-0 space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Full Name</Label>
                  <Input data-testid={AUTH.nameInput} required={tab === "signup"} value={name} onChange={(e) => setName(e.target.value)} className="bg-slate-800 border-white/10" />
                </div>
              </TabsContent>

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-400">Email</Label>
                <Input data-testid={AUTH.emailInput} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-slate-800 border-white/10" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-400">Password</Label>
                <Input data-testid={AUTH.passwordInput} type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="bg-slate-800 border-white/10" />
              </div>

              {error && (
                <p data-testid={AUTH.errorMessage} className="rounded-lg border border-rose-600/50 bg-rose-950/50 px-3 py-2 text-sm text-rose-300">{error}</p>
              )}

              <Button data-testid={AUTH.submitButton} type="submit" disabled={loading} className="w-full bg-orange-600 text-white hover:bg-orange-700 active:scale-[0.98]">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {tab === "login" ? "Sign In" : "Create Account"}
              </Button>
            </form>

            <div className="my-4 flex items-center gap-3 text-xs text-slate-500">
              <div className="h-px flex-1 bg-white/10" /> OR <div className="h-px flex-1 bg-white/10" />
            </div>

            <Button data-testid={AUTH.googleButton} onClick={startGoogleLogin} variant="outline" className="w-full border-white/15 bg-white text-slate-900 hover:bg-slate-100">
              <img alt="" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="mr-2 h-4 w-4" />
              Continue with Google
            </Button>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
